import React, { useEffect, useMemo, useState } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
} from "../../../components/Component";
import { Button, Card, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Spinner } from "reactstrap";
import swal from "sweetalert";
import http from "../../../utils/http-common";
import { canManageAdmins } from "../../../utils/adminPermissions";

const ZONES = [
  { id: "finance", label: "Finance" },
  { id: "activity", label: "Activité" },
  { id: "directory", label: "Répertoire" },
  { id: "configuration", label: "Configuration" },
];
const SENSITIVE = [
  { id: "refund", label: "Remboursement" },
  { id: "ban_delete", label: "Bannir / supprimer" },
  { id: "api_keys", label: "Clés API" },
  { id: "manage_admins", label: "Gérer les admins" },
];

function initials(name) {
  return String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}

export default function RbacAdmins() {
  const me = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);
  const allowed = canManageAdmins(me);

  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [zones, setZones] = useState({});
  const [sensitive, setSensitive] = useState({});
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRoleId, setInviteRoleId] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, r] = await Promise.all([
        http.get("/api/v1/admin/rbac/admins"),
        http.get("/api/v1/admin/rbac/roles"),
      ]);
      setRows(a.data?.data || []);
      setRoles(r.data?.data || []);
    } catch (e) {
      swal("Error", e.response?.data?.error || "Failed to load admins", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allowed) load();
    else setLoading(false);
  }, [allowed]);

  const pick = async (row) => {
    if (String(row.id).startsWith("invite:")) {
      setSelected(row);
      setDetail(null);
      return;
    }
    setSelected(row);
    try {
      const res = await http.get(`/api/v1/admin/rbac/admins/${row.id}`);
      const d = res.data?.data;
      setDetail(d);
      setZones(d?.summary?.zones || {});
      setSensitive(d?.summary?.sensitive || {});
    } catch (e) {
      swal("Error", e.response?.data?.error || "Failed to load admin", "error");
    }
  };

  const savePerms = async () => {
    if (!detail?.id) return;
    setSaving(true);
    try {
      await http.put(`/api/v1/admin/rbac/admins/${detail.id}`, { zones, sensitive });
      swal("Saved", "Permissions updated", "success");
      await load();
      await pick({ id: detail.id });
    } catch (e) {
      swal("Error", e.response?.data?.error || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const invite = async () => {
    setSaving(true);
    try {
      await http.post("/api/v1/admin/rbac/admins/invite", {
        email: inviteEmail.trim(),
        roleId: inviteRoleId,
      });
      swal("Sent", "Invitation email sent", "success");
      setInviteOpen(false);
      setInviteEmail("");
      setInviteRoleId("");
      await load();
    } catch (e) {
      swal("Error", e.response?.data?.error || "Invite failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const act = async (fn, okMsg) => {
    try {
      await fn();
      if (okMsg) swal("OK", okMsg, "success");
      await load();
      setSelected(null);
      setDetail(null);
    } catch (e) {
      swal("Error", e.response?.data?.error || "Action failed", "error");
    }
  };

  if (!allowed) {
    return (
      <>
        <Head title="Admins" />
        <Content>
          <Block>
            <p className="text-soft">You do not have permission to manage admins.</p>
          </Block>
        </Content>
      </>
    );
  }

  return (
    <>
      <Head title="Admins & permissions" />
      <Content>
        <BlockHead>
          <BlockHeadContent>
            <BlockTitle page>Admins & permissions</BlockTitle>
            <BlockDes>
              <p>Only a super admin can create admins or change permissions.</p>
            </BlockDes>
          </BlockHeadContent>
          <div>
            <Button color="primary" onClick={() => setInviteOpen(true)}>
              + Invite admin
            </Button>
          </div>
        </BlockHead>

        {loading ? (
          <Spinner />
        ) : (
          <div className="d-flex flex-wrap" style={{ gap: 16 }}>
            <Card className="card-bordered flex-grow-1" style={{ minWidth: 280, maxWidth: 520 }}>
              {(rows || []).map((row) => (
                <div
                  key={row.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => pick(row)}
                  onKeyDown={(e) => e.key === "Enter" && pick(row)}
                  className={`d-flex align-items-center px-3 py-3 border-bottom ${
                    selected?.id === row.id ? "bg-lighter" : ""
                  } ${row.isSuperAdmin ? "bg-primary-dim" : ""}`}
                  style={{ cursor: "pointer", gap: 12 }}
                >
                  <div
                    className="user-avatar bg-light"
                    style={{ width: 34, height: 34, fontSize: 12, fontWeight: 600 }}
                  >
                    <span>{initials(row.name)}</span>
                  </div>
                  <div className="flex-grow-1">
                    <strong className="d-block">{row.name}</strong>
                    <span className="text-soft small">
                      {row.roles?.map((r) => r.name).join(" · ") || row.email}
                    </span>
                  </div>
                  {row.isSuperAdmin ? (
                    <span className="badge badge-primary">Super admin</span>
                  ) : (
                    <span
                      className={`badge badge-${
                        row.status === "invited"
                          ? "warning"
                          : row.status === "disabled"
                          ? "danger"
                          : "success"
                      }`}
                    >
                      {row.status === "invited"
                        ? "Invited"
                        : row.status === "disabled"
                        ? "Disabled"
                        : "Active"}
                    </span>
                  )}
                </div>
              ))}
            </Card>

            <Card className="card-bordered p-3" style={{ width: 320 }}>
              {!detail && selected?.invitationId ? (
                <>
                  <h6>{selected.email}</h6>
                  <p className="text-soft small">Pending invitation</p>
                  <Button
                    size="sm"
                    color="primary"
                    className="mr-2"
                    onClick={() =>
                      act(
                        () =>
                          http.post(
                            `/api/v1/admin/rbac/invitations/${selected.invitationId}/resend`
                          ),
                        "Invitation resent"
                      )
                    }
                  >
                    Resend
                  </Button>
                  <Button
                    size="sm"
                    color="danger"
                    outline
                    onClick={() =>
                      act(() =>
                        http.post(
                          `/api/v1/admin/rbac/invitations/${selected.invitationId}/revoke`
                        )
                      )
                    }
                  >
                    Revoke
                  </Button>
                </>
              ) : !detail ? (
                <p className="text-soft small mb-0">Select an admin to edit permissions.</p>
              ) : (
                <>
                  <h6 className="mb-1">{detail.name} — permissions</h6>
                  <p className="text-soft small">
                    Role: {detail.roles?.map((r) => r.name).join(", ") || "—"}
                  </p>
                  <div className="text-uppercase text-soft small font-weight-bold mb-2">Zones</div>
                  {ZONES.map((z) => (
                    <label key={z.id} className="d-flex justify-content-between align-items-center py-1">
                      <span>{z.label}</span>
                      <span>
                        <input
                          type="checkbox"
                          className="mr-1"
                          checked={!!zones[z.id]?.view}
                          onChange={(e) =>
                            setZones((prev) => ({
                              ...prev,
                              [z.id]: { ...prev[z.id], view: e.target.checked },
                            }))
                          }
                        />{" "}
                        view{" "}
                        <input
                          type="checkbox"
                          className="ml-2 mr-1"
                          checked={!!zones[z.id]?.act}
                          onChange={(e) =>
                            setZones((prev) => ({
                              ...prev,
                              [z.id]: { ...prev[z.id], act: e.target.checked },
                            }))
                          }
                        />{" "}
                        act
                      </span>
                    </label>
                  ))}
                  <div className="text-uppercase text-danger small font-weight-bold mt-3 mb-2">
                    Sensitive actions
                  </div>
                  {SENSITIVE.map((s) => (
                    <label key={s.id} className="d-flex justify-content-between py-1">
                      <span>{s.label}</span>
                      <input
                        type="checkbox"
                        checked={!!sensitive[s.id]}
                        onChange={(e) =>
                          setSensitive((prev) => ({ ...prev, [s.id]: e.target.checked }))
                        }
                      />
                    </label>
                  ))}
                  <div className="alert alert-warning small mt-3 mb-2 py-2">
                    Each sensitive action is logged. The last super admin cannot be removed.
                  </div>
                  <Button color="primary" block disabled={saving} onClick={savePerms}>
                    {saving ? "Saving…" : "Save"}
                  </Button>
                  <div className="mt-3 d-flex flex-wrap" style={{ gap: 8 }}>
                    {detail.active === false ? (
                      <Button
                        size="sm"
                        outline
                        onClick={() =>
                          act(
                            () => http.post(`/api/v1/admin/rbac/admins/${detail.id}/enable`),
                            "Admin reactivated"
                          )
                        }
                      >
                        Reactivate
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        outline
                        color="warning"
                        onClick={() =>
                          act(
                            () => http.post(`/api/v1/admin/rbac/admins/${detail.id}/disable`),
                            "Admin disabled"
                          )
                        }
                      >
                        Disable
                      </Button>
                    )}
                    <Button
                      size="sm"
                      outline
                      color="danger"
                      onClick={() =>
                        act(() => http.delete(`/api/v1/admin/rbac/admins/${detail.id}`))
                      }
                    >
                      Delete
                    </Button>
                  </div>
                </>
              )}
            </Card>
          </div>
        )}

        <Modal isOpen={inviteOpen} toggle={() => setInviteOpen(false)}>
          <ModalHeader toggle={() => setInviteOpen(false)}>Invite admin</ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label>Role (required)</Label>
              <Input
                type="select"
                value={inviteRoleId}
                onChange={(e) => setInviteRoleId(e.target.value)}
              >
                <option value="">Select a role…</option>
                {roles.map((r) => (
                  <option key={r._id} value={r._id}>
                    {r.name}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <Button
              color="primary"
              disabled={saving || !inviteEmail || !inviteRoleId}
              onClick={invite}
            >
              Send invitation
            </Button>
          </ModalBody>
        </Modal>
      </Content>
    </>
  );
}
